import React, { useEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";
import styles from "./chat.module.scss";
import Markdown from "react-markdown";
import CodeEditor from "../codeEditor";
import { translations } from "../../../../i18n";

/** Minimum time a status line stays on screen, so bursts of events stay readable. */
const STATUS_MIN_DWELL_MS = 800;
/** Silence after which the status line switches to a reassuring message. */
const STATUS_STALE_MS = 15000;

export interface SeekStreamEvent {
  type: string;
  agent?: string;
  content?: string;
  name?: string;
  args?: Record<string, unknown>;
  status?: string;
  data?: string;
  raw?: string;
  [key: string]: unknown;
}

interface ChainOfThoughtProps {
  querying: boolean;
  events: Array<Record<string, unknown>>;
  /** Expanded while querying; collapses when final answer is generated */
  defaultExpanded: boolean;
}

/**
 * Reduce a streamed thought to a single line: first sentence only, whitespace collapsed.
 * Backend already strips tool names, code and markdown before sending these.
 */
function toStatusLine(text: string): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (!clean) return "";
  const sentence = clean.match(/^[\s\S]*?[.!?](?=\s|$)/);
  return sentence ? sentence[0] : clean;
}

/**
 * Holds each status line on screen for at least STATUS_MIN_DWELL_MS before showing the
 * next one, so a burst of events reads as progress rather than flicker. Intermediate
 * targets are dropped — only the latest one is shown when the dwell window closes.
 */
function useDwelledStatus(target: string): string {
  const [displayed, setDisplayed] = useState(target);
  const targetRef = useRef(target);
  const lastChangeRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  targetRef.current = target;

  useEffect(() => {
    if (target === displayed || timerRef.current !== null) return;

    const wait = Math.max(0, STATUS_MIN_DWELL_MS - (Date.now() - lastChangeRef.current));
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      lastChangeRef.current = Date.now();
      setDisplayed(targetRef.current);
    }, wait);
  }, [target, displayed]);

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    []
  );

  return displayed;
}

interface LiveStatusProps {
  events: Array<Record<string, unknown>>;
  t: Record<string, string>;
}

const LiveStatus: React.FC<LiveStatusProps> = ({ events, t }) => {
  const target = useMemo(() => {
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i] as SeekStreamEvent;
      if (e.type === "function_call") return t["seek.cot.status.analysing"];
      if (e.type === "token") {
        const line = toStatusLine(e.content || "");
        if (line) return line;
      }
    }
    return t["seek.cot.status.starting"];
  }, [events, t]);

  const displayed = useDwelledStatus(target);

  // Nothing new for a while — reassure rather than sit on a stale thought.
  const [stale, setStale] = useState(false);
  useEffect(() => {
    setStale(false);
    const id = window.setTimeout(() => setStale(true), STATUS_STALE_MS);
    return () => window.clearTimeout(id);
  }, [events.length]);

  const text = stale ? t["seek.cot.status.still_working"] : displayed;

  return (
    <div className={styles.cotStatus} aria-live="polite">
      <span className={styles.cotStatusDots} aria-hidden>
        <span className={styles.cotStatusDot} />
        <span className={styles.cotStatusDot} />
        <span className={styles.cotStatusDot} />
      </span>
      <span key={text} className={styles.cotStatusText}>
        {text}
      </span>
    </div>
  );
};

export const ChainOfThought: React.FC<ChainOfThoughtProps> = ({
  querying,
  events,
  defaultExpanded
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  useEffect(() => {
    setIsExpanded(defaultExpanded);
  }, [defaultExpanded]);

  const displayEvents = events.filter((e): e is Record<string, unknown> =>
    e.type === "token" ||
    e.type === "function_call" ||
    e.type === "function_response"
  );

  if (displayEvents.length === 0 && !querying) return null;

  const t = translations.translation as unknown as Record<string, string>;
  const fmt = (key: string, ...args: (string | number)[]) =>
    translations.formatString(t[key], ...args) as string;

  const renderEvent = (event: Record<string, unknown>, index: number) => {
    const e = event as SeekStreamEvent;
    if (e.type === "token") {
      return (
        <div key={index} className={styles.cotStep}>
          <span className={styles.cotStepLabel}>{t["seek.cot.thought"]}</span>
          <p className={styles.cotStepContent}>
            <Markdown>{e.content || ""}</Markdown>
          </p>
        </div>
      );
    }
    if (e.type === "function_call") {
      const toolName = e.name || t["seek.cot.unknown"];
      let argsStr =
        e.args && Object.keys(e.args).length > 0
          ? JSON.stringify(e.args, null, 2)
          : null;

      if (toolName === "execute_code") {
        const code = e.args?.analysis_code || argsStr;
        if (code) {
          argsStr = code as string;
        }
      }

      return (
        <div key={index} className={styles.cotStep}>
          <span className={styles.cotStepLabel}>
            {fmt("seek.cot.tool_named", toolName)}
          </span>
          {toolName !== "execute_code" && (
            <pre className={styles.cotStepCode}>{argsStr}</pre>
          )}

          {toolName === "execute_code" && (
            <CodeEditor
              pythonCode={argsStr || ""}
              name={(e.name as string) || (t["seek.cot.unknown"] as string)}
              aggregationCode={
                typeof e.args?.aggregation_code_map === "string"
                  ? e.args.aggregation_code_map
                  : e.args?.aggregation_code_map
                    ? JSON.stringify(e.args.aggregation_code_map, null, 2)
                    : ""
              }
            />
          )}
        </div>
      );
    }
    if (e.type === "function_response") {
      return (
        <div key={index} className={styles.cotStep}>
          <span className={styles.cotStepLabel}>
            {e.name
              ? fmt("seek.cot.result_named", e.name)
              : t["seek.cot.result"]}
          </span>
          <p className={styles.cotStepContent}>{e.status}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div className={styles.chainOfThought}>
        <button
          type="button"
          className={styles.cotHeader}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
        >
          <span className={styles.cotTitle}>
            {querying ? t["seek.cot.thinking"] : t["seek.cot.chain_of_thought"]}
          </span>
          {displayEvents.length > 0 && (
            <span className={styles.cotBadge}>
              {fmt("seek.cot.steps", displayEvents.length)}
            </span>
          )}
          <span
            className={cx(styles.cotChevron, { [styles.cotChevronOpen]: isExpanded })}
            aria-hidden
          />
        </button>
        {isExpanded && (
          <div className={styles.cotBody}>
            {displayEvents.map((event, index) => renderEvent(event, index))}
          </div>
        )}
      </div>
      {querying && !isExpanded && <LiveStatus events={events} t={t} />}
    </>
  );
};
